import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserDomainRole } from '@/types/User';

export type AdminUserDetailPageState = {
  // UC-33 — "Deactivate" confirm dialog state (state-shape §2 `pendingDeactivateConfirm`).
  pendingDeactivateConfirm: boolean;
  pendingRoleChangeConfirm: { newRole: UserDomainRole } | null;
  // UC-60 cross-listed — Add-to-Team modal state (state-shape §2).
  addToTeamModalOpen: boolean;
  addToTeamDraft: {
    teamId: string;
    // UC-62 compound — "Also designate as Team Lead" toggle.
    asTeamLead: boolean;
  };
  // UC-61 cross-listed — "Remove from team" confirm on a team-membership row
  // (state-shape §2 `admin-user-detail-page` `pendingRemoveFromTeamConfirm`).
  // Stores teamName alongside teamId to avoid an extra fetch in the dialog.
  pendingRemoveFromTeamConfirm: { teamId: string; teamName: string } | null;
  // UC-62 cross-listed — inline "Designate as Lead" confirm on a team-membership row.
  pendingDesignateAsTeamLeadConfirm: { teamId: string; teamName: string } | null;
  // UC-63 cross-listed — inline "Remove as Team Lead" confirm on a team-membership
  // row where the user is currently a lead (§5.web-portal.1 / §5.web-portal.3).
  pendingRemoveAsTeamLeadConfirm: { teamId: string; teamName: string } | null;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: AdminUserDetailPageState = {
  pendingDeactivateConfirm: false,
  pendingRoleChangeConfirm: null,
  addToTeamModalOpen: false,
  addToTeamDraft: { teamId: '', asTeamLead: false },
  pendingRemoveFromTeamConfirm: null,
  pendingDesignateAsTeamLeadConfirm: null,
  pendingRemoveAsTeamLeadConfirm: null,
  submitInProgress: false,
  lastErrorKey: null,
};

export const adminUserDetailPageSlice = createSlice({
  name: 'adminUserDetailPage',
  initialState,
  reducers: {
    // ── UC-33 — Deactivate confirm dialog ─────────────────────────────────
    openDeactivateConfirm(state) {
      state.pendingDeactivateConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeDeactivateConfirm(state) {
      state.pendingDeactivateConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    openRoleChangeConfirm(state, action: PayloadAction<{ newRole: UserDomainRole }>) {
      state.pendingRoleChangeConfirm = { newRole: action.payload.newRole };
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeRoleChangeConfirm(state) {
      state.pendingRoleChangeConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    // ── UC-60 cross-listed — Add-to-Team modal ────────────────────────────
    openAddToTeamModal(state) {
      state.addToTeamModalOpen = true;
      state.addToTeamDraft = initialState.addToTeamDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeAddToTeamModal(state) {
      state.addToTeamModalOpen = false;
      state.addToTeamDraft = initialState.addToTeamDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setAddToTeamDraftTeamId(state, action: PayloadAction<string>) {
      state.addToTeamDraft.teamId = action.payload;
    },
    setAddToTeamDraftAsTeamLead(state, action: PayloadAction<boolean>) {
      state.addToTeamDraft.asTeamLead = action.payload;
    },
    // ── UC-63 cross-listed — remove as team lead on a membership row ─────
    openRemoveAsTeamLeadConfirm(
      state,
      action: PayloadAction<{ teamId: string; teamName: string }>,
    ) {
      state.pendingRemoveAsTeamLeadConfirm = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeRemoveAsTeamLeadConfirm(state) {
      state.pendingRemoveAsTeamLeadConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-61 cross-listed — remove from team confirm on a membership row ──
    openRemoveFromTeamConfirm(
      state,
      action: PayloadAction<{ teamId: string; teamName: string }>,
    ) {
      state.pendingRemoveFromTeamConfirm = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeRemoveFromTeamConfirm(state) {
      state.pendingRemoveFromTeamConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-62 cross-listed — designate as team lead on a membership row ───
    openDesignateAsTeamLeadConfirm(
      state,
      action: PayloadAction<{ teamId: string; teamName: string }>,
    ) {
      state.pendingDesignateAsTeamLeadConfirm = action.payload;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeDesignateAsTeamLeadConfirm(state) {
      state.pendingDesignateAsTeamLeadConfirm = null;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    resetPage() {
      return initialState;
    },
  },
});

export const {
  openDeactivateConfirm,
  closeDeactivateConfirm,
  openRoleChangeConfirm,
  closeRoleChangeConfirm,
  openAddToTeamModal,
  closeAddToTeamModal,
  setAddToTeamDraftTeamId,
  setAddToTeamDraftAsTeamLead,
  openRemoveAsTeamLeadConfirm,
  closeRemoveAsTeamLeadConfirm,
  openRemoveFromTeamConfirm,
  closeRemoveFromTeamConfirm,
  openDesignateAsTeamLeadConfirm,
  closeDesignateAsTeamLeadConfirm,
  setSubmitInProgress,
  setLastErrorKey,
  resetPage,
} = adminUserDetailPageSlice.actions;

type AppRootState = {
  adminUserDetailPage: AdminUserDetailPageState;
};

export const selectAdminUserDetailPage = (state: AppRootState) => state.adminUserDetailPage;
